import { useState } from 'react';
import { useLang } from '../hooks/useLang';
import { LegalPageLayout } from '../components/LegalPageLayout';

const content = {
  EN: {
    back: "Back to Home",
    title: "Cookie Policy",
    updated: "KEH-LG-03 · Version 1.0 · 27 September 2026 · Approved by CEO",
    sections: [
      {
        heading: "1. About This Policy",
        body: `This Cookie Policy (the "Policy") explains what cookies are, which cookies we use on our websites, for what purpose and how you can control them.

Controller: Köber Energy Holding S.R.L., 18 Cluceru Udricani street, 4th floor, Bucharest, Romania · Trade Register J2025042093001, CUI 51959799 · office@koberholding.com

Websites covered: https://www.koberholding.com/ · https://koberenergy.com/ · https://utvromania.com/ · https://bessintegrator.com/ · https://gridpowerx.com/

Legal framework: The use of cookies is governed by Law no. 506/2004 on the processing of personal data and the protection of privacy in the electronic communications sector (which transposes Directive 2002/58/EC, "ePrivacy"), by Regulation (EU) 2016/679 (GDPR) and by the guidelines of ANSPDCP and of the European Data Protection Board. Cookies that are not strictly necessary are placed only with your prior, freely given, specific and informed consent.`,
      },
      {
        heading: "2. What Cookies and Similar Technologies Are",
        body: `A cookie is a small text file that a website saves in your browser when you visit it. On subsequent visits, the browser sends the cookie back to the website, which can thus "recognise" it. Cookies cannot run programs and cannot access other files on your device.

Session cookies — Are deleted automatically when the browser is closed.
Persistent cookies — Remain on the device until they expire or until you delete them.
First-party cookies — Are placed by the website you are visiting.
Third-party cookies — Are placed by other domains (for example, analytics tools, embedded video content, social networks).
Similar technologies — Tracking pixels, web beacons, local storage, session storage, device identifiers — they have similar functions and are treated in this Policy in the same way as cookies.`,
      },
      {
        heading: "3. The Categories of Cookies We Use",
        body: `3.1 STRICTLY NECESSARY COOKIES
They are indispensable for the operation of the websites and cannot be disabled from the consent banner. Legal basis: legitimate interest; no consent required (Art. 4(6) of Law no. 506/2004).

cookie_consent / cc_settings · Köber website · Stores your choices regarding cookies · Duration: 12 months
PHPSESSID / session · Köber website · Maintains the browsing session and form security · Duration: Session
csrf_token · Köber website · Protects forms against CSRF attacks · Duration: Session
_cf_bm / cf_clearance · Cloudflare (if used) · Protection against bots and attacks · Duration: 30 minutes – 1 year
wp_lang / language · Köber website · Stores the selected language · Duration: 12 months

3.2 ANALYTICS COOKIES
They help us understand how the websites are used (pages visited, duration, traffic source, device) in order to improve them. The data is aggregated; IP addresses are anonymised where the tool allows it. Legal basis: consent.

_ga, _ga_* · Google Analytics 4 · Distinguishes users and sessions, measures audience · Duration: 13 months
_gid · Google Analytics · Distinguishes users · Duration: 24 hours
_pk_id, _pk_ses · Matomo (if used) · Audience measurement with data hosted in the EU · Duration: 13 months / 30 minutes

3.3 FUNCTIONAL COOKIES
They enable additional functions, such as storing preferences or displaying embedded content (maps, videos). Legal basis: consent.

YSC, VISITOR_INFO1_LIVE · YouTube (Google) · Playback of embedded videos and storing playback preferences · Duration: Session / 6 months
NID · Google Maps · Display of embedded maps · Duration: 6 months
lang, li_gc · LinkedIn · Embedded LinkedIn buttons and content · Duration: Session / 6 months

3.4 MARKETING COOKIES
They serve to display relevant content and advertisements on our websites and on third-party platforms and to measure the effectiveness of campaigns. They may create a profile of your interests and may be shared with advertising platforms. Legal basis: consent.

_gcl_au, IDE, test_cookie · Google Ads / DoubleClick · Conversion measurement and remarketing · Duration: 3 months – 13 months
_fbp, fr · Meta (Facebook, Instagram) · Campaign measurement and remarketing · Duration: 3 months
li_sugr, bcookie, UserMatchHistory · LinkedIn · B2B advertising and campaign measurement · Duration: 1 – 12 months

The detailed and up-to-date list of cookies active on each website is displayed in the consent banner, under the "Details" section, and is reviewed at least once every 6 months. We do not currently use marketing cookies on the corporate websites unless this is expressly indicated in the consent banner of the website concerned.`,
      },
      {
        heading: "4. How You Make and Change Your Choices",
        body: `4.1 THE CONSENT BANNER
On your first visit to each website a consent banner is displayed through which you can:
• Accept all cookies;
• Refuse all non-essential cookies, with a single click, as easily as accepting;
• Configure each category individually.

Non-essential cookies are not placed before you have made your choice. You may revisit your choices at any time via the "Cookie Settings" link in the footer of each website. We will ask for your consent again no later than after 12 months or when we add new categories.

4.2 BROWSER SETTINGS
You can block or delete cookies from your browser settings. Please note that blocking strictly necessary cookies may affect the operation of the websites.

Google Chrome · Settings → Privacy and security → Cookies and other site data
Mozilla Firefox · Settings → Privacy & Security → Cookies and Site Data
Microsoft Edge · Settings → Cookies and site permissions
Apple Safari · Preferences → Privacy → Manage Website Data
Mobile devices · Settings of the browser application or of the operating system

4.3 OPTING OUT OF THIRD-PARTY TOOLS
Google Analytics can also be disabled through the official browser add-on (https://tools.google.com/dlpage/gaoptout). Advertising preferences can be managed at https://www.youronlinechoices.com/ro/ and in the settings of your Google, Meta and LinkedIn accounts.`,
      },
      {
        heading: "5. Personal Data and Transfers",
        body: `Cookies may contain or generate personal data (online identifiers, IP address). The processing of such data is described in the Privacy Policy (KEH-LG-02), including your rights and how to exercise them.

Some providers (Google, Meta, LinkedIn) may transfer data to the United States; transfers are made on the basis of the EU-U.S. Data Privacy Framework or the Standard Contractual Clauses, in accordance with Chapter 4 of the Privacy Policy.`,
      },
      {
        heading: "6. Updates",
        body: `We may update the Policy when we change the tools used or when legislation and the guidelines of the authorities so require. The version in force, with the date of the last update, is published on each website.`,
      },
      {
        heading: "7. Contact",
        body: `Controller: Köber Energy Holding S.R.L.
Address: 18 Cluceru Udricani street, 4th floor, Bucharest, Romania
E-mail: office@koberholding.com
Web: www.koberholding.com
Supervisory authority: ANSPDCP · www.dataprotection.ro

This version enters into force on 27 September 2026. Document code: KEH-LG-03 · Version 1.0`,
      },
    ],
  },
  RO: {
    back: "Înapoi la Pagina Principală",
    title: "Politica de Cookie-uri",
    updated: "KEH-LG-03 · Versiunea 1.0 · 27 Septembrie 2026 · Aprobată de CEO",
    sections: [
      {
        heading: "1. Despre Această Politică",
        body: `Această Politică de Cookie-uri explică ce sunt cookie-urile, ce cookie-uri utilizăm pe site-urile noastre web, în ce scop și cum le puteți controla.

Operator: Köber Energy Holding S.R.L., Str. Cluceru Udricani nr. 18, etaj 4, București, România · Registrul Comerțului J2025042093001, CUI 51959799 · office@koberholding.com

Site-uri web acoperite: www.koberholding.com · koberenergy.com · utvromania.com · bessintegrator.com · gridpowerx.com

Cadru legal: Utilizarea cookie-urilor este reglementată de Legea nr. 506/2004 privind prelucrarea datelor cu caracter personal și protecția vieții private în sectorul comunicațiilor electronice (care transpune Directiva 2002/58/CE, „ePrivacy"), de Regulamentul (UE) 2016/679 (RGPD) și de ghidurile ANSPDCP și ale Comitetului European pentru Protecția Datelor. Cookie-urile care nu sunt strict necesare sunt plasate numai cu consimțământul dvs. prealabil, liber exprimat, specific și informat.`,
      },
      {
        heading: "2. Ce Sunt Cookie-urile și Tehnologiile Similare",
        body: `Un cookie este un fișier text mic pe care un site web îl salvează în browserul dvs. atunci când îl vizitați. La vizitele ulterioare, browserul trimite înapoi cookie-ul site-ului web, care îl poate astfel „recunoaște". Cookie-urile nu pot rula programe și nu pot accesa alte fișiere de pe dispozitivul dvs.

Cookie-uri de sesiune — Sunt șterse automat când browserul este închis.
Cookie-uri persistente — Rămân pe dispozitiv până la expirare sau până când le ștergeți.
Cookie-uri proprii (first-party) — Sunt plasate de site-ul web pe care îl vizitați.
Cookie-uri terțe (third-party) — Sunt plasate de alte domenii (de exemplu, instrumente de analiză, conținut video încorporat, rețele sociale).
Tehnologii similare — Pixeli de urmărire, web beacon-uri, stocare locală, stocare de sesiune, identificatori de dispozitiv — acestea au funcții similare și sunt tratate în această Politică în același mod ca și cookie-urile.`,
      },
      {
        heading: "3. Categoriile de Cookie-uri pe Care le Utilizăm",
        body: `3.1 COOKIE-URI STRICT NECESARE
Sunt indispensabile pentru funcționarea site-urilor web și nu pot fi dezactivate din bannerul de consimțământ. Temei legal: interes legitim; nu este necesar consimțământul (Art. 4(6) din Legea nr. 506/2004).

cookie_consent / cc_settings · Site Köber · Stochează alegerile dvs. privind cookie-urile · Durată: 12 luni
PHPSESSID / session · Site Köber · Menține sesiunea de navigare și securitatea formularelor · Durată: Sesiune
csrf_token · Site Köber · Protejează formularele împotriva atacurilor CSRF · Durată: Sesiune
_cf_bm / cf_clearance · Cloudflare (dacă este utilizat) · Protecție împotriva boților și atacurilor · Durată: 30 minute – 1 an
wp_lang / language · Site Köber · Stochează limba selectată · Durată: 12 luni

3.2 COOKIE-URI DE ANALIZĂ
Ne ajută să înțelegem cum sunt utilizate site-urile web (pagini vizitate, durată, sursă trafic, dispozitiv) pentru a le îmbunătăți. Datele sunt agregate; adresele IP sunt anonimizate acolo unde instrumentul permite. Temei legal: consimțământ.

_ga, _ga_* · Google Analytics 4 · Distinge utilizatorii și sesiunile, măsoară audiența · Durată: 13 luni
_gid · Google Analytics · Distinge utilizatorii · Durată: 24 ore
_pk_id, _pk_ses · Matomo (dacă este utilizat) · Măsurarea audienței cu date găzduite în UE · Durată: 13 luni / 30 min

3.3 COOKIE-URI FUNCȚIONALE
Permit funcții suplimentare, cum ar fi stocarea preferințelor sau afișarea de conținut încorporat (hărți, videoclipuri). Temei legal: consimțământ.

YSC, VISITOR_INFO1_LIVE · YouTube (Google) · Redarea videoclipurilor încorporate · Durată: Sesiune / 6 luni
NID · Google Maps · Afișarea hărților încorporate · Durată: 6 luni
lang, li_gc · LinkedIn · Butoane și conținut LinkedIn încorporat · Durată: Sesiune / 6 luni

3.4 COOKIE-URI DE MARKETING
Servesc la afișarea de conținut și reclame relevante. Temei legal: consimțământ.

_gcl_au, IDE, test_cookie · Google Ads / DoubleClick · Măsurarea conversiilor și remarketing · Durată: 3 luni – 13 luni
_fbp, fr · Meta (Facebook, Instagram) · Măsurarea campaniilor și remarketing · Durată: 3 luni
li_sugr, bcookie, UserMatchHistory · LinkedIn · Publicitate B2B și măsurarea campaniilor · Durată: 1 – 12 luni`,
      },
      {
        heading: "4. Cum Vă Exercitați și Modificați Alegerile",
        body: `4.1 BANNERUL DE CONSIMȚĂMÂNT
La prima dvs. vizită pe fiecare site web se afișează un banner de consimțământ prin care puteți:
• Accepta toate cookie-urile;
• Refuza toate cookie-urile neesențiale, cu un singur clic, la fel de ușor ca acceptarea;
• Configura individual fiecare categorie.

Cookie-urile neesențiale nu sunt plasate înainte de a face alegerea dvs. Vă puteți revizui alegerile oricând prin linkul „Setări Cookie-uri" din subsolul fiecărui site web.

4.2 SETĂRILE BROWSERULUI
Puteți bloca sau șterge cookie-urile din setările browserului dvs.

Google Chrome · Setări → Confidențialitate și securitate → Cookie-uri și alte date ale site-urilor
Mozilla Firefox · Setări → Confidențialitate și Securitate → Cookie-uri și Date ale Site-urilor
Microsoft Edge · Setări → Cookie-uri și permisiuni site
Apple Safari · Preferințe → Confidențialitate → Gestionare Date Site-uri
Dispozitive mobile · Setările aplicației de browser sau ale sistemului de operare

4.3 DEZACTIVAREA INSTRUMENTELOR TERȚE
Google Analytics poate fi dezactivat prin add-on-ul oficial pentru browser (https://tools.google.com/dlpage/gaoptout). Preferințele publicitare pot fi gestionate la https://www.youronlinechoices.com/ro/.`,
      },
      {
        heading: "5. Date cu Caracter Personal și Transferuri",
        body: `Cookie-urile pot conține sau genera date cu caracter personal (identificatori online, adresă IP). Prelucrarea acestor date este descrisă în Politica de Confidențialitate (KEH-LG-02), inclusiv drepturile dvs. și modul de exercitare a acestora.

Unii furnizori (Google, Meta, LinkedIn) pot transfera date în Statele Unite; transferurile se fac pe baza Cadrului de confidențialitate a datelor UE-SUA sau a Clauzelor Contractuale Standard, în conformitate cu Capitolul 4 din Politica de Confidențialitate.`,
      },
      {
        heading: "6. Actualizări",
        body: `Putem actualiza Politica atunci când schimbăm instrumentele utilizate sau când legislația și ghidurile autorităților o impun. Versiunea în vigoare, cu data ultimei actualizări, este publicată pe fiecare site web.`,
      },
      {
        heading: "7. Contact",
        body: `Operator: Köber Energy Holding S.R.L.
Adresă: Str. Cluceru Udricani nr. 18, etaj 4, București, România
E-mail: office@koberholding.com
Web: www.koberholding.com
Autoritate de supraveghere: ANSPDCP · www.dataprotection.ro

Această versiune intră în vigoare la 27 Septembrie 2026. Cod document: KEH-LG-03 · Versiunea 1.0`,
      },
    ],
  },
};

export default function CookiePolicy() {
  const [lang, setLang] = useLang();
  const handleSetLang = setLang;
  return <LegalPageLayout c={content[lang]} lang={lang} setLang={handleSetLang} testIdPrefix="cookie" />;
}
